// src/views/BuildersList.jsx
import React, { useEffect, useState, useMemo } from "react";
import useFindBuilderStore from "../store/useFindBuilderStore.js";
import { useNavigate } from "react-router-dom";
import {
  Compass,
  MapPin,
  ShieldCheck,
  Calendar,
  Briefcase,
  UserCheck,
  Search,
  RotateCcw,
  SlidersHorizontal,
  Heart,
  ChevronDown,
  MapPinIcon as DirectionsMapPinIcon,
} from "lucide-react";
import { toast } from "react-hot-toast";
import GenericModal from "../components/common/GenericModal.jsx";
import MobileBottomNav from "../components/common/MobileBottomNav";

const PLACEHOLDER_LOGO = "https://placehold.co/72x72/f8fafc/94a3b8?text=Logo";
const DEFAULT_COVER =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=900&q=80";

const EXPERIENCE_OPTIONS = [
  { id: "10plus", label: "10+ Years" },
  { id: "5-10", label: "5–10 Years" },
  { id: "2-5", label: "2–5 Years" },
  { id: "lt2", label: "Less than 2 Years" },
];

const PER_PAGE_OPTIONS = [12, 24, 48];

const BuildersList = () => {
  const {
    setSelectedState,
    setSelectedDistrict,
    setSelectedTaluk,
    fetchLocationData,
    builders,
    isLoading,
    selectedDistrict,
    selectedTaluk,
    fetchBuilders,
    fetchMappedDistricts,
    fetchMappedTaluks,
    fetchDefaultServices,
    mappedDistricts,
    mappedTaluks,
    defaultServices,
    filterExperience,
    filterServiceIds,
    filterVerifiedOnly,
    setFilterExperience,
    setFilterServiceIds,
    setFilterVerifiedOnly,
    clearFilters,
    pagination,
  } = useFindBuilderStore();

  const navigate = useNavigate();

  const [draftDistrict, setDraftDistrict] = useState(() => localStorage.getItem("selectedDistrict") || "");
  const [draftTaluk, setDraftTaluk] = useState(() => localStorage.getItem("selectedTaluk") || "");
  const [pendingFilters, setPendingFilters] = useState({
    experience: [],
    serviceIds: [],
    verifiedOnly: false,
  });
  const [sortBy, setSortBy] = useState("popular");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(1000);
  const [favorites, setFavorites] = useState(() => new Set());
  const [isDirectionsModalOpen, setIsDirectionsModalOpen] = useState(false);
  const [selectedBuilderForDirections, setSelectedBuilderForDirections] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    const savedDistrict = localStorage.getItem("selectedDistrict");
    const savedTaluk = localStorage.getItem("selectedTaluk");
    if (savedDistrict) {
      setSelectedDistrict(savedDistrict);
      setDraftDistrict(savedDistrict);
    }
    if (savedTaluk) {
      setSelectedTaluk(savedTaluk);
      setDraftTaluk(savedTaluk);
    }
  }, [setSelectedDistrict, setSelectedTaluk]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setSelectedState("Tamil Nadu");
    fetchLocationData();
    fetchMappedDistricts("Tamil Nadu");
    fetchDefaultServices();
  }, [fetchLocationData, fetchMappedDistricts, fetchDefaultServices, setSelectedState]);

  useEffect(() => {
    if (draftDistrict) {
      fetchMappedTaluks(draftDistrict);
    }
  }, [draftDistrict, fetchMappedTaluks]);

  useEffect(() => {
    setPendingFilters({
      experience: [...filterExperience],
      serviceIds: [...filterServiceIds],
      verifiedOnly: filterVerifiedOnly,
    });
  }, [filterExperience, filterServiceIds, filterVerifiedOnly]);

  useEffect(() => {
    if (selectedDistrict && selectedTaluk) {
      fetchBuilders({ enquiry: true, page: currentPage, limit: perPage });
    }
  }, [
    selectedDistrict,
    selectedTaluk,
    filterExperience,
    filterServiceIds,
    filterVerifiedOnly,
    currentPage,
    perPage,
    fetchBuilders,
  ]);

  const handleSearchLocation = () => {
    if (!draftDistrict || !draftTaluk) {
      toast.error("Please select both District and Taluk.");
      return;
    }
    setSelectedDistrict(draftDistrict);
    setSelectedTaluk(draftTaluk);
    localStorage.setItem("selectedDistrict", draftDistrict);
    localStorage.setItem("selectedTaluk", draftTaluk);
    setCurrentPage(1);
  };

  const handleClearLocation = () => {
    setDraftDistrict("");
    setDraftTaluk("");
    setSelectedDistrict("");
    setSelectedTaluk("");
    localStorage.removeItem("selectedDistrict");
    localStorage.removeItem("selectedTaluk");
    useFindBuilderStore.setState({
      builders: [],
      mappedTaluks: { taluks: [] },
      pagination: { total: 0, currentPage: 1, totalPages: 0 },
    });
  };

  const toggleExperience = (id) => {
    setPendingFilters((p) => ({
      ...p,
      experience: p.experience.includes(id)
        ? p.experience.filter((x) => x !== id)
        : [...p.experience, id],
    }));
  };

  const toggleService = (id) => {
    setPendingFilters((p) => ({
      ...p,
      serviceIds: p.serviceIds.includes(id)
        ? p.serviceIds.filter((x) => x !== id)
        : [...p.serviceIds, id],
    }));
  };

  const applyFilters = () => {
    setFilterExperience(pendingFilters.experience);
    setFilterServiceIds(pendingFilters.serviceIds);
    setFilterVerifiedOnly(pendingFilters.verifiedOnly);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    clearFilters();
    setPendingFilters({ experience: [], serviceIds: [], verifiedOnly: false });
    setCurrentPage(1);
  };

  const sortedBuilders = useMemo(() => {
    const list = [...(builders || [])];
    if (sortBy === "name") {
      list.sort((a, b) =>
        (a.company_name || "").localeCompare(b.company_name || "")
      );
    } else if (sortBy === "experience") {
      list.sort(
        (a, b) => (b.years_experience ?? 0) - (a.years_experience ?? 0)
      );
    }
    return list;
  }, [builders, sortBy]);

  const formatLocation = (builder) => {
    const taluk = builder.builder_primary_taluk || builder.taluk;
    const district = builder.builder_primary_district || builder.district;
    if (taluk && district) return `${taluk}, ${district}`;
    return builder.village || taluk || district || "Location unavailable";
  };

  const getCoverUrl = (builder) => builder.cover_image || DEFAULT_COVER;

  const toggleFavorite = (id) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGetDirections = (builder) => {
    if (builder.latitude && builder.longitude) {
      setSelectedBuilderForDirections(builder);
      setIsDirectionsModalOpen(true);
    } else {
      toast.error("Location coordinates are not available for this builder.");
    }
  };

  const proceedToGoogleMaps = () => {
    if (
      selectedBuilderForDirections?.latitude &&
      selectedBuilderForDirections?.longitude
    ) {
      const { latitude, longitude } = selectedBuilderForDirections;
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`,
        "_blank"
      );
      setIsDirectionsModalOpen(false);
      setSelectedBuilderForDirections(null);
    }
  };

  const totalFound = pagination?.total ?? builders?.length ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const showResults = selectedDistrict && selectedTaluk;

  const locationTitle = showResults
    ? `Builders in ${selectedDistrict} District, ${selectedTaluk} Taluk`
    : "Find Builders";

  const rangeStart = totalFound === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalFound);

  return (
    <div className="min-h-screen bg-[#eef1f6] pb-20 lg:pb-6">
      {/* Location search bar */}
      <div className="bg-white text-gray-800 shadow-sm border-b border-gray-200">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 py-3">
          <p className="text-center text-[11px] sm:text-xs text-gray-500 mb-3">
            Join us with Ducktail once you choose your Builder to get support access from Ducktail.
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-4">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block font-semibold">
                  District
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    className="w-full h-10 pl-9 pr-8 rounded-md border border-gray-300 bg-white text-gray-800 text-sm appearance-none outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                    value={draftDistrict}
                    onChange={(e) => {
                      setDraftDistrict(e.target.value);
                      setDraftTaluk("");
                    }}
                    disabled={!mappedDistricts?.districts?.length}
                  >
                    <option value="">Select District</option>
                    {mappedDistricts.districts?.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 block font-semibold">
                  Taluk
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    className="w-full h-10 pl-9 pr-8 rounded-md border border-gray-300 bg-white text-gray-800 text-sm appearance-none outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 disabled:bg-gray-100 disabled:text-gray-400"
                    value={draftTaluk}
                    onChange={(e) => setDraftTaluk(e.target.value)}
                    disabled={!draftDistrict || !mappedTaluks?.taluks?.length}
                  >
                    <option value="">Select Taluk</option>
                    {mappedTaluks.taluks?.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <button
                type="button"
                onClick={handleSearchLocation}
                className="flex-1 lg:flex-none h-10 px-5 rounded-md bg-[#f0a500] hover:bg-[#e09500] text-[#152238] font-bold text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <Search className="w-4 h-4" />
                Search Builders
              </button>
              <button
                type="button"
                onClick={handleClearLocation}
                className="h-10 px-3 text-gray-500 hover:text-gray-900 text-xs font-semibold flex items-center gap-1 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main body — tight margins */}
      <div className="max-w-[1536px] mx-auto px-4 sm:px-6 py-6">
        {!showResults ? (
          <div className="bg-white rounded-lg border border-gray-200 text-center py-14 text-gray-500 text-sm">
            Select District and Taluk, then click <strong>Search Builders</strong>.
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Filter sidebar */}
            <aside className="w-full lg:w-[280px] shrink-0">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden lg:sticky lg:top-3">
                <div 
                  className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80 cursor-pointer lg:cursor-default"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                >
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-gray-600" />
                    <h2 className="font-bold text-gray-800 text-sm">Filter Builders</h2>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 lg:hidden transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
                </div>
                <div className={`lg:block ${showMobileFilters ? 'block' : 'hidden'}`}>
                  <div className="p-4 space-y-4 max-h-[calc(100vh-220px)] overflow-y-auto">
                  <section>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Experience
                    </p>
                    <div className="space-y-1.5">
                      {EXPERIENCE_OPTIONS.map((opt) => (
                        <label
                          key={opt.id}
                          className="flex items-center gap-2 cursor-pointer text-[13px] text-gray-700 hover:text-gray-900 py-0.5"
                        >
                          <input
                            type="checkbox"
                            checked={pendingFilters.experience.includes(opt.id)}
                            onChange={() => toggleExperience(opt.id)}
                            className="rounded border-gray-300 text-[#152238] focus:ring-[#152238]"
                          />
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </section>

                  <section className="border-t border-gray-100 pt-4">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Service
                    </p>
                    <div className="space-y-1.5 max-h-36 overflow-y-auto">
                      {defaultServices.length === 0 ? (
                        <p className="text-xs text-gray-400">No services configured.</p>
                      ) : (
                        defaultServices.map((svc) => (
                          <label
                            key={svc.id}
                            className="flex items-center gap-2 cursor-pointer text-[13px] text-gray-700 py-0.5"
                          >
                            <input
                              type="checkbox"
                              checked={pendingFilters.serviceIds.includes(svc.id)}
                              onChange={() => toggleService(svc.id)}
                              className="rounded border-gray-300 text-[#152238] focus:ring-[#152238]"
                            />
                            <Briefcase className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="truncate">{svc.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </section>

                  <section className="border-t border-gray-100 pt-4">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Verified Builders
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer text-[13px] text-gray-700">
                      <input
                        type="checkbox"
                        checked={pendingFilters.verifiedOnly}
                        onChange={(e) =>
                          setPendingFilters((p) => ({
                            ...p,
                            verifiedOnly: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-[#152238] focus:ring-[#152238]"
                      />
                      <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                      Verified Only
                    </label>
                  </section>
                </div>
                <div className="p-3 border-t border-gray-100 flex gap-2 bg-gray-50/50">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="flex-1 py-2 text-xs font-semibold border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      applyFilters();
                      setShowMobileFilters(false);
                    }}
                    className="flex-1 py-2 text-xs font-semibold bg-[#152238] text-white rounded-md hover:bg-[#1c2d4d]"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
              </div>
            </aside>

            {/* Results */}
            <main className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900 leading-snug">
                    {locationTitle}
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isLoading
                      ? "Loading…"
                      : `${totalFound} Builder${totalFound === 1 ? "" : "s"} found`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-500 whitespace-nowrap">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="h-8 pl-2 pr-7 text-xs border border-gray-200 rounded-md bg-white text-gray-700 outline-none focus:border-[#152238]"
                  >
                    <option value="popular">Most Popular</option>
                    <option value="name">Name (A–Z)</option>
                    <option value="experience">Most Experience</option>
                  </select>
                </div>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-[#152238]" />
                </div>
              ) : sortedBuilders.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-start">
                    {sortedBuilders.map((builder) => (
                      <article
                        key={builder.builder_id}
                        className="w-full max-w-[350px] sm:max-w-none mx-auto bg-white rounded-xl border border-gray-200/90 shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col"
                      >
                        {/* Cover Image */}
                        <div className="relative w-full h-40 bg-gray-200 shrink-0">
                          <img
                            src={getCoverUrl(builder)}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = DEFAULT_COVER;
                            }}
                          />
                          {builder.is_verified && (
                            <span className="absolute top-2 left-2 inline-flex items-center gap-0.5 bg-[#1a3a6b] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-sm">
                              <ShieldCheck className="w-2.5 h-2.5" />
                              VERIFIED
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleFavorite(builder.builder_id)}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/20 hover:bg-black/35 flex items-center justify-center"
                            aria-label="Favorite"
                          >
                            <Heart
                              className={`w-3.5 h-3.5 ${
                                favorites.has(builder.builder_id)
                                  ? "fill-red-500 text-red-500"
                                  : "text-white"
                              }`}
                            />
                          </button>
                          <div className="absolute -bottom-3.5 left-3 w-14 h-14 rounded-md border-2 border-white bg-white shadow overflow-hidden z-10">
                            <img
                              src={builder.profile_image || PLACEHOLDER_LOGO}
                              alt=""
                              className="w-full h-full object-contain p-0.5"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = PLACEHOLDER_LOGO;
                              }}
                            />
                          </div>
                        </div>

                        {/* Body — tight, portrait card (image 2 proportions) */}
                        <div className="pt-5 px-3 pb-3">
                          <div className="flex items-start gap-1 mb-0.5">
                            <h3 className="font-bold text-gray-900 text-[13px] leading-snug line-clamp-1 flex-1">
                              {builder.company_name || "Company Unavailable"}
                            </h3>
                            {builder.is_verified && (
                              <UserCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                            )}
                          </div>
                          <p className="text-[11px] text-gray-500 flex items-center gap-0.5 mb-2 line-clamp-1">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="capitalize">{formatLocation(builder)}</span>
                          </p>

                          {/* Stats — value on top, label below (reference card) */}
                          <div className="grid grid-cols-3 gap-0 border-y border-gray-100 py-2 mb-2.5 text-center">
                            <div className="px-0.5 border-r border-gray-100 last:border-r-0">
                              <p className="text-[11px] font-bold text-gray-900 leading-none flex items-center justify-center gap-0.5">
                                <Calendar className="w-3 h-3 text-amber-500 shrink-0" />
                                {builder.years_experience != null
                                  ? builder.years_experience >= 10
                                    ? `${builder.years_experience}+`
                                    : builder.years_experience
                                  : "—"}
                              </p>
                              <p className="text-[8px] text-gray-400 mt-0.5 leading-tight">Years Exp.</p>
                            </div>
                            <div className="px-0.5 border-r border-gray-100">
                              <p
                                className="text-[10px] font-bold text-gray-900 leading-tight line-clamp-2 min-h-[22px] flex items-center justify-center"
                                title={(builder.services || []).map((s) => s.name).join(", ")}
                              >
                                {(builder.services || []).length
                                  ? `${builder.services[0].name.split(" ")[0]}${builder.services.length > 1 ? ` +${builder.services.length - 1}` : ""}`
                                  : "—"}
                              </p>
                              <p className="text-[8px] text-gray-400 mt-0.5">Service</p>
                            </div>
                            <div className="px-0.5">
                              <p className="text-[11px] font-bold leading-none min-h-[22px] flex items-center justify-center gap-0.5 text-gray-900">
                                <Briefcase className="w-3 h-3 text-gray-500 shrink-0" />
                                {builder.completed_projects_count || 0}
                              </p>
                              <p className="text-[8px] text-gray-400 mt-0.5">Projects</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-1.5">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/buildersinfo/${builder.builder_id}`)
                              }
                              className="py-2 text-[11px] font-semibold border border-[#2563eb] text-[#2563eb] rounded-md hover:bg-blue-50"
                            >
                              View Profile
                            </button>
                            <button
                              type="button"
                              onClick={() => handleGetDirections(builder)}
                              className="py-2 text-[11px] font-semibold bg-[#0B1527] text-white rounded-md hover:bg-[#152238] flex items-center justify-center gap-1"
                            >
                              <Compass className="w-3.5 h-3.5" />
                              Direction
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-14 text-gray-500 text-sm bg-white rounded-lg border border-gray-200">
                  No builders match your filters for this location.
                </div>
              )}
            </main>
          </div>
        )}
      </div>

      <GenericModal
        isOpen={isDirectionsModalOpen}
        onClose={() => {
          setIsDirectionsModalOpen(false);
          setSelectedBuilderForDirections(null);
        }}
        title="Confirm Directions"
        size="max-w-md"
      >
        {selectedBuilderForDirections && (
          <div>
            <p className="text-sm text-gray-700 mb-1">You are about to get directions to:</p>
            <div className="bg-gray-50 p-3 rounded-md border border-gray-200 text-xs mb-4">
              <p className="font-semibold text-gray-800 text-sm">
                {selectedBuilderForDirections.company_name}
              </p>
              <p>{formatLocation(selectedBuilderForDirections)}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsDirectionsModalOpen(false);
                  setSelectedBuilderForDirections(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={proceedToGoogleMaps}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs font-medium flex items-center gap-1.5"
              >
                <DirectionsMapPinIcon className="w-3.5 h-3.5" />
                Proceed to Maps
              </button>
            </div>
          </div>
        )}
      </GenericModal>
      <MobileBottomNav />
    </div>
  );
};

export default BuildersList;
